import { zodResolver } from "@hookform/resolvers/zod";
import UserValidator from "Validators/Common/User";
import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import AuthRoutes from "Routes/Components/Auth";
import NoAuthRoutes from "Routes/Components/NoAuth";

import { HTTPErrorCallback } from "Helpers/Error";

import UseAuth from "Hooks/UseAuth";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import CaptchaWidget from "Components/Common/CaptchaWidget";
import FormWrapper from "Components/Common/FormWrapper";
import Notifications from "Components/Common/Notifications";
import Button from "Components/Global/Buttons";
import Input from "Components/Global/Form/Input";
import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

import { useTheme } from "styled-components";

const HeadRight = () => {
  const theme = useTheme();

  const { parsedJWT, authorized, storeToken, logout } = UseAuth();
  const { api } = UseServices();
  const { loading, setLoading } = UseGlobalLoading();
  const { showNotification } = UseNotification();
  const navigate = useNavigate();

  const { hasAlerts, hasPendingNotifications } = UseNotification();

  /*
   * O ponto vermelho do sino, por PERFIL (corrigido em 20/09/2026).
   *
   * A parte 4 trocou a fonte deste header por `hasAlerts`, que é computado só
   * para o ADMIN (`/notifications/alerts` exige `read:all_projects` e o polling
   * nem roda para o comum). Resultado: o proponente e o patrocinador nunca mais
   * viram o ponto, mesmo com notificação nova no banco. Este header é o da área
   * COMUM — a fonte dele é `hasPendingNotifications`; o `hasAlerts` fica para
   * quando o admin navega por aqui.
   */
  const is_admin = parsedJWT?.user?.profile_type_id === 1;
  const showNotificationDot = is_admin ? hasAlerts : hasPendingNotifications;

  const [openNotifications, setOpenNotifications] = useState(false);
  const CAPTCHA_ENABLED = process.env.REACT_APP_ENABLE_CAPTCHA !== "false";
  // Token padrão: null quando o CAPTCHA está ativo (precisa resolver o
  // Turnstile) ou o código de bypass quando desativado (dev/homolog).
  const DEFAULT_CAPTCHA_TOKEN = CAPTCHA_ENABLED
    ? null
    : process.env.REACT_APP_CAPTCHA_CODE_BYPASS;
  const [captchaToken, setCaptchaToken] = useState(DEFAULT_CAPTCHA_TOKEN);
  const turnstileRef = useRef(null);

  const currentPage = `/${window.location.pathname.split("/").pop()}`;

  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const openAuthModal = () => setAuthOpen(true);

    window.addEventListener("open-auth-modal", openAuthModal);

    return () => {
      window.removeEventListener("open-auth-modal", openAuthModal);
    };
  }, []);

  const formSchema = z.object({
    login: UserValidator.email,
    password: UserValidator.password,
  });

  const methods = useForm({
    resolver: zodResolver(formSchema),
  });
  const { register } = methods;

  const submit = async (data) => {
    if (!captchaToken) {
      showNotification({
        type: "error",
        message: "Por favor, complete a verificação de segurança.",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/sessions/login", {
        ...data,
        captcha_token: captchaToken,
        captcha_date: new Date(),
      });

      storeToken(response.data.token, true);

      setLoading(false);
      setAuthOpen(false);
      setCaptchaToken(DEFAULT_CAPTCHA_TOKEN);
    } catch (error) {
      setLoading(false);
      // Restaura o token padrão para reabilitar o botão numa nova tentativa.
      // Com CAPTCHA ativo, isso volta a null e o Turnstile é resetado abaixo;
      // com CAPTCHA desativado, mantém o código de bypass (botão segue ativo).
      setCaptchaToken(DEFAULT_CAPTCHA_TOKEN);
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      HTTPErrorCallback(error);
    }
  };

  const handleOpenNotifications = () => {
    openNotifications
      ? setOpenNotifications(false)
      : setOpenNotifications(true);
  };

  const is_sponsor = parsedJWT?.user?.is_sponsor;

  return (
    <S.Wrapper>
      {showLoginModal && (
        <S.LoginModalOverlay onClick={() => setShowLoginModal(false)}>
          <S.LoginModalContent onClick={(e) => e.stopPropagation()}>
            <S.LoginModalClose onClick={() => setShowLoginModal(false)}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </S.LoginModalClose>

            <S.LoginModalHeader>
              <h3>Crie sua conta ou faca login</h3>
              <p>
                Para enviar seu projeto e ter acesso a todas as funcionalidades
                da plataforma, voce precisa estar autenticado.
              </p>
            </S.LoginModalHeader>

            <S.LoginModalButtons>
              <Button
                variant="primary"
                text="Criar conta"
                width="100%"
                onClick={() => {
                  setShowLoginModal(false);
                  navigate(AuthRoutes.Register.path);
                }}
              />
              <Button
                variant="secondary"
                text="Ja tenho conta"
                width="100%"
                onClick={() => {
                  setShowLoginModal(false);
                  setAuthOpen(true);
                }}
              />
            </S.LoginModalButtons>
            <S.ForgotPasswordLink
              onClick={() => {
                setShowLoginModal(false);
                navigate(NoAuthRoutes.ForgotPassword.path);
              }}
            >
              Esqueci minha senha
            </S.ForgotPasswordLink>
          </S.LoginModalContent>
        </S.LoginModalOverlay>
      )}

      <S.Side>
        <S.Row>
          <S.PageLink
            onClick={() => navigate(AuthRoutes.Dashboard.path)}
            $active={currentPage === AuthRoutes.Dashboard.path}
          >
            Home
          </S.PageLink>
          <S.PageLink
            onClick={() => setOpen(true)}
            $active={[
              AuthRoutes.CreateProject.path,
              AuthRoutes.Productions.path,
            ].includes(currentPage)}
          >
            Serviços
          </S.PageLink>
          {open ? (
            <>
              <S.InvisibleBackground onClick={() => setOpen(false)} />
              <S.Modal id="modal" auth={authorized}>
                {!is_sponsor ? (
                  <S.ModalItem
                    onClick={() => {
                      if (!authorized) {
                        setOpen(false);
                        setShowLoginModal(true);
                        return;
                      }

                      navigate(AuthRoutes.CreateProject.path);
                      setAuthOpen(false);
                      setOpen(false);
                    }}
                    $active={currentPage === AuthRoutes.CreateProject.path}
                  >
                    Captação de Recursos
                  </S.ModalItem>
                ) : null}

                <S.ModalItem
                  onClick={() => {
                    navigate(AuthRoutes.ConsultingAndManagement.path);
                    setAuthOpen(false);
                    setOpen(false);
                  }}
                  $active={
                    currentPage === AuthRoutes.ConsultingAndManagement.path
                  }
                >
                  Consultoria e Gestão de Recursos Incentivados
                </S.ModalItem>
                <S.ModalItem
                  onClick={() => {
                    navigate(AuthRoutes.Productions.path);
                    setAuthOpen(false);
                    setOpen(false);
                  }}
                  $active={currentPage === AuthRoutes.Productions.path}
                >
                  Produções artísticas
                </S.ModalItem>
              </S.Modal>
            </>
          ) : null}
          <S.PageLink
            onClick={() => {
              navigate(AuthRoutes.About.path);
              setAuthOpen(false);
            }}
            $active={currentPage === AuthRoutes.About.path}
          >
            Sobre nós
          </S.PageLink>
        </S.Row>
      </S.Side>

      <S.Side>
        {authorized ? (
          <>
            <S.Notifications onClick={handleOpenNotifications}>
              <GetIcon name="Bell" color={theme.colors.neutral.white} />
              {showNotificationDot && (
                // `title`: o ponto é só cor e não tem texto próprio — sem um nome
                // acessível ele é invisível para leitor de tela e para teste.
                // Mesma convenção do badge das sessões do projeto.
                <S.NotificationDot title="Há alertas pendentes" />
              )}
            </S.Notifications>

            <S.Link
              onClick={() => {
                navigate(AuthRoutes.ListProjects.path);
                setAuthOpen(false);
                setOpen(false);
              }}
            >
              Meus Projetos
            </S.Link>

            {openNotifications && (
              <Notifications setOpenNotifications={setOpenNotifications} />
            )}
          </>
        ) : null}

        <S.Auth>
          <S.Row onClick={() => setAuthOpen(!authOpen)}>
            <GetIcon name="User" color={"#fff"} />
            <p>{authorized ? parsedJWT?.user?.full_name : "Entrar"}</p>
          </S.Row>

          {authOpen ? (
            <S.AuthModal auth={authorized}>
              <S.ModalHeader>
                <GetIcon
                  name="X"
                  color={"#fff"}
                  onClick={() => setAuthOpen(false)}
                />
              </S.ModalHeader>

              {authorized ? (
                <S.Row
                  onClick={() => {
                    logout();
                    navigate(AuthRoutes.Dashboard.path);
                    window.location.reload();
                  }}
                >
                  <GetIcon name="Logout" color={"#fff"} />
                  <span>Sair</span>
                </S.Row>
              ) : (
                <>
                  <h2>Login</h2>

                  <FormWrapper {...methods} submit={submit}>
                    <Input
                      {...register("login")}
                      label="Email:"
                      placeholder="Email"
                    />

                    <Input
                      {...register("password")}
                      label="Senha:"
                      placeholder="Senha"
                      type="password"
                    />

                    <S.CaptchaWrapper>
                      <CaptchaWidget
                        ref={turnstileRef}
                        onSuccess={setCaptchaToken}
                        options={{ theme: "dark", size: "normal" }}
                      />
                    </S.CaptchaWrapper>

                    <Button
                      type="submit"
                      text="Entrar"
                      disabled={loading || !captchaToken}
                    />

                    <Button
                      outline
                      type="button"
                      text="Cadastre-se"
                      onClick={() => {
                        setAuthOpen(false);
                        navigate(AuthRoutes.Register.path);
                      }}
                      disabled={loading}
                    />
                    <S.ForgotPasswordLink
                      type="button"
                      onClick={() => {
                        setAuthOpen(false);
                        navigate(NoAuthRoutes.ForgotPassword.path);
                      }}
                    >
                      Esqueci minha senha
                    </S.ForgotPasswordLink>
                  </FormWrapper>
                </>
              )}
            </S.AuthModal>
          ) : null}
        </S.Auth>
      </S.Side>
    </S.Wrapper>
  );
};

export default HeadRight;
